import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { notifIcon, timeAgo } from "@/lib/notifications";
import { Bell, CheckCheck } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function NotificationBell() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/notifications"); setItems(data); } catch {}
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  const unread = items.filter((n) => !n.read).length;

  const open = async (n) => {
    if (!n.read) { try { await api.put(`/notifications/${n.id}/read`); } catch {} }
    setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    if (n.link) navigate(n.link);
  };
  const markAll = async () => {
    try { await api.put("/notifications/read-all"); } catch {}
    setItems((prev) => prev.map((x) => ({ ...x, read: true })));
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="relative flex h-9 w-9 items-center justify-center rounded-md text-navy transition-colors hover:bg-secondary" data-testid="topbar-notifications-btn">
          <Bell className="h-5 w-5" strokeWidth={1.75} />
          {unread > 0 && (
            <span data-testid="notif-unread-count" className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-gold px-1 text-[10px] font-bold text-navy">{unread}</span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <p className="font-display text-lg text-navy">Notifications</p>
          {unread > 0 && <button onClick={markAll} data-testid="notif-mark-all" className="flex items-center gap-1 text-xs font-medium text-navy hover:text-gold"><CheckCheck className="h-3.5 w-3.5" />Mark all read</button>}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 && <p className="px-4 py-8 text-center text-sm text-muted-foreground">No notifications yet.</p>}
          {items.slice(0, 12).map((n) => {
            const Icon = notifIcon(n.type);
            return (
              <button key={n.id} onClick={() => open(n)} data-testid={`notif-item-${n.id}`}
                className={`flex w-full items-start gap-3 border-b border-border px-4 py-3 text-left transition-colors hover:bg-secondary/50 ${n.read ? "" : "bg-gold/5"}`}>
                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-navy/5 text-navy"><Icon className="h-4 w-4" /></div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-navy">{n.title}</p>
                  <p className="truncate text-xs text-muted-foreground">{n.message}</p>
                  <p className="text-[11px] text-muted-foreground/70">{timeAgo(n.created_at)}</p>
                </div>
                {!n.read && <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-gold" />}
              </button>
            );
          })}
        </div>
        <button onClick={() => navigate("/notifications")} className="w-full border-t border-border py-2.5 text-center text-sm font-medium text-navy hover:bg-secondary/50" data-testid="notif-see-all">See all notifications</button>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
